import type { ExpressionSpecification } from 'mapbox-gl';
import { FONT_SIZE_100 } from '@alltrails/denali/tokens';

/**
 * https://www.figma.com/design/Hfw8SV8IylHR4A3nnQ7Fvs/Map-Icons?node-id=481-1698&p=f&m=dev
 * Trailhead pins draw their art from the Mapbox style sprite (not SVGs created at runtime).
 * This helper creates the correct string that matches the name of the svg in the sprite (ex: 'trailhead-circle-cluster-closed').
 *
 * A trailhead with one trail does not have a count banner. Trailheads with two or
 * more trails use the trailhead icon with a count banner, which come in three widths, one per digit
 * of the trail count. The count banner is drawn by the layer's `text-field` because an icon cannot
 * render feature data. Collapsed and selected pins are fixed-size icons with no count.
 */

export type TrailheadPinVariant = 'closed' | 'saved' | 'completed' | 'default';

export type TrailheadVariantExpressions = {
  isClosedExpression: ExpressionSpecification;
  isCompletedExpression: ExpressionSpecification;
  isSavedExpression: ExpressionSpecification;
};

// One name per digit count: under 10, under 100, and everything larger.
const clusterImageNames: Record<TrailheadPinVariant, [string, string, string]> = {
  closed: ['trailhead-circle-cluster-closed', 'trailhead-circle-cluster-closed2', 'trailhead-circle-cluster-closed3'],
  saved: ['trailhead-circle-cluster-favorite', 'trailhead-circle-cluster-favorite2', 'trailhead-circle-cluster-favorite3'],
  completed: ['trailhead-circle-cluster-completed', 'trailhead-circle-cluster-completed2', 'trailhead-circle-cluster-completed3'],
  default: ['trailhead-circle-cluster', 'trailhead-circle-cluster2', 'trailhead-circle-cluster3']
};

const selectedImageNames: Record<TrailheadPinVariant, string> = {
  closed: 'trailhead-selected-closed',
  saved: 'trailhead-selected-favorite',
  completed: 'trailhead-selected-completed',
  default: 'trailhead-selected'
};

const collapsedImageNames: Record<TrailheadPinVariant, string> = {
  closed: 'trailhead-dot-minus',
  saved: 'trailhead-dot-heart',
  completed: 'trailhead-dot-checked',
  default: 'trailhead-dot-default'
};

/**
 * Closed wins over saved, and saved over completed, so a trailhead whose trails are all closed
 * reads as closed no matter what else is true of it. This matches how the trail pins rank the same
 * three states.
 */
function buildVariantExpression(
  { isClosedExpression, isCompletedExpression, isSavedExpression }: TrailheadVariantExpressions,
  imageForVariant: (variant: TrailheadPinVariant) => string | ExpressionSpecification
): ExpressionSpecification {
  return [
    'case',
    isClosedExpression,
    imageForVariant('closed'),
    isSavedExpression,
    imageForVariant('saved'),
    isCompletedExpression,
    imageForVariant('completed'),
    imageForVariant('default')
  ];
}

function buildClusterImageExpression(variant: TrailheadPinVariant, trailCountExpression: ExpressionSpecification): ExpressionSpecification {
  const [oneDigit, twoDigit, threeDigit] = clusterImageNames[variant];

  return [
    'case',
    ['==', trailCountExpression, 1],
    'trailhead-circle-default',
    ['<', trailCountExpression, 10],
    oneDigit,
    ['<', trailCountExpression, 100],
    twoDigit,
    threeDigit
  ];
}

/** Expanded pins: the circle cluster sized to the count, or the selected pin when active. */
export function buildTrailheadIconImage(
  variantExpressions: TrailheadVariantExpressions,
  isActiveExpression: ExpressionSpecification,
  trailCountExpression: ExpressionSpecification
): ExpressionSpecification {
  return [
    'case',
    isActiveExpression,
    buildVariantExpression(variantExpressions, variant => selectedImageNames[variant]),
    buildVariantExpression(variantExpressions, variant => buildClusterImageExpression(variant, trailCountExpression))
  ];
}

/** Collapsed pins: a dot badged with the trailhead's state. */
export function buildTrailheadCollapsedIconImage(variantExpressions: TrailheadVariantExpressions): ExpressionSpecification {
  return buildVariantExpression(variantExpressions, variant => collapsedImageNames[variant]);
}

/** Selected or hovered pins, collapsed or not, share one icon and never show a count. */
export function buildTrailheadSelectedIconImage(variantExpressions: TrailheadVariantExpressions): ExpressionSpecification {
  return buildVariantExpression(variantExpressions, variant => selectedImageNames[variant]);
}

/**
 * `text-field` is typed as a string, so the count has to be converted rather than passed as the
 * number the count expression resolves to. Selected pins and single-trail pins drop the count.
 */
export function buildTrailheadCountTextField(
  isActiveExpression: ExpressionSpecification,
  trailCountExpression: ExpressionSpecification
): ExpressionSpecification {
  return ['case', isActiveExpression, '', ['==', trailCountExpression, 1], '', ['to-string', trailCountExpression]];
}

/**
 * `text-offset` is used to horizontally and vertically offset the text from the icon.
 * Vertical offset is constant, but horizontal offset is dynamic based on whether the in is closed, completed, saved, or default.
 */
export function buildTrailheadTextOffset(variantExpressions: TrailheadVariantExpressions): ExpressionSpecification {
  return [
    'case',
    variantExpressions.isClosedExpression,
    [-6 / FONT_SIZE_100, 2.5 / FONT_SIZE_100],
    variantExpressions.isCompletedExpression,
    [-6 / FONT_SIZE_100, 2.5 / FONT_SIZE_100],
    variantExpressions.isSavedExpression,
    [-6 / FONT_SIZE_100, 2.5 / FONT_SIZE_100],
    [-0.5 / FONT_SIZE_100, 2.5 / FONT_SIZE_100]
  ];
}

/**
 * `icon-offset` is used to horizontally and vertically offset the icon from its anchor.
 * Horizontal offset is always 0, but vertical offset is dynamic based on whether the trailhead is active and or collapsed.
 * We have to vertically offset the icon because the different icons have different heights.
 */
export function buildTrailheadIconOffset(isActiveExpression: ExpressionSpecification): ExpressionSpecification {
  return ['case', isActiveExpression, [0, -16], [0, -14]];
}

export function buildTrailheadIconCollapsedOffset(isActiveExpression: ExpressionSpecification): ExpressionSpecification {
  return ['case', isActiveExpression, [0, -16], [0, 0]];
}
